/**
 * Reset Password Modal Component
 * Multi-step password reset flow with phone/email and OTP verification
 * 
 * @author Varun Pandey
 */

import React, { useState, useRef, useEffect } from 'react';
import { X, ArrowLeft, Mail, Phone, CheckCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { PhoneInput } from '../ui/PhoneInput';
import { OTPInput } from '../ui/OTPInput';
import { SimilarProfile } from './SimilarProfiles';
import { AuthService, getAuthErrorMessage } from '../../services/authService';
import toast from 'react-hot-toast';
import { INDIAN_PHONE_ERROR_MESSAGE, isValidIndianPhoneNumber } from '../../utils/phone';

type ResetStep = 'contact' | 'otp' | 'password' | 'success';
type ContactMethod = 'phone' | 'email';

export interface ResetPasswordContactPrefill {
  email?: string;
  phone?: string;
}

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: SimilarProfile | null;
  onSuccess: () => void;
  /** Pre-fill email/phone on the contact step (e.g. logged-in user changing password). */
  contactPrefill?: ResetPasswordContactPrefill;
  /** `settings`: change-password copy; `default`: forgot-password / signup flow. */
  flowContext?: 'default' | 'settings';
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  profile,
  onSuccess,
  contactPrefill,
  flowContext = 'default',
}) => {
  const [step, setStep] = useState<ResetStep>('contact');
  const [contactMethod, setContactMethod] = useState<ContactMethod>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('contact');
      const preEmail = contactPrefill?.email?.trim() ?? '';
      const prePhone = contactPrefill?.phone?.replace(/\s+/g, '').trim() ?? '';
      setContactMethod(preEmail ? 'email' : 'phone');
      setPhoneNumber(prePhone);
      setEmail(preEmail);
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
    }
  }, [isOpen, contactPrefill?.email, contactPrefill?.phone]);

  // Auto-focus inputs
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (step === 'contact') {
          if (contactMethod === 'phone' && phoneRef.current) {
            phoneRef.current.focus();
          } else if (contactMethod === 'email' && emailRef.current) {
            emailRef.current.focus();
          }
        } else if (step === 'password' && newPasswordRef.current) {
          newPasswordRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, step, contactMethod]);

  if (!isOpen || !profile) return null;

  const validateContact = () => {
    const newErrors: Record<string, string> = {};
    if (contactMethod === 'phone') {
      if (!phoneNumber) {
        newErrors.phone = 'Phone number is required';
      } else if (!isValidIndianPhoneNumber(phoneNumber)) {
        newErrors.phone = INDIAN_PHONE_ERROR_MESSAGE;
      }
    } else {
      if (!email) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors: Record<string, string> = {};
    if (!newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else {
      const hasUpperCase = /[A-Z]/.test(newPassword);
      const hasNumber = /[0-9]/.test(newPassword);
      const hasSpecialChar = /[^A-Za-z0-9]/.test(newPassword);
      if (!hasUpperCase || !hasNumber || !hasSpecialChar) {
        newErrors.newPassword = 'Password must include uppercase, number, and special character';
      }
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getTo = (): string => (contactMethod === 'phone' ? phoneNumber : email);

  const handleSendOTP = async () => {
    if (!validateContact()) {
      if (contactMethod === 'phone' && phoneNumber && !isValidIndianPhoneNumber(phoneNumber)) {
        toast.error(INDIAN_PHONE_ERROR_MESSAGE);
      }
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      await AuthService.requestPasswordResetOtp(getTo());
      toast.success('OTP sent successfully');
      setStep('otp');
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Failed to send OTP. Please try again.');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (otpValue: string) => {
    setIsLoading(true);
    setErrors({});
    try {
      await AuthService.verifyPasswordResetOtp(getTo(), otpValue);
      setStep('password');
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Invalid OTP. Please try again.');
      setErrors({ otp: message });
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!validatePassword()) return;

    setIsLoading(true);
    setErrors({});
    try {
      await AuthService.resetPasswordPut(getTo(), newPassword);
      setStep('success');
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Failed to reset password. Please try again.');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'otp') {
      setStep('contact');
      setOtp('');
    } else if (step === 'password') {
      setStep('otp');
    }
    setErrors({});
  };

  const formatPhoneDisplay = (phone: string) => {
    if (!phone || phone.length < 3) return phone;
    const masked = phone.slice(-3).padStart(10, 'X');
    return `${masked.slice(0, 5)} ${masked.slice(5, 10)}`;
  };

  const maskEmail = (emailStr: string) => {
    const [local, domain] = emailStr.split('@');
    if (local.length <= 3) return emailStr;
    return `${local.slice(0, 2)}${'*'.repeat(local.length - 3)}${local.slice(-1)}@${domain}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 rounded-2xl overflow-hidden shadow-xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C]/50">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#2C2C2E]">
          <div className="flex items-center gap-3">
            {(step === 'otp' || step === 'password') && (
              <button
                onClick={handleBack}
                className="text-[#8E8E93] hover:text-white transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-lg font-semibold text-white">
              {flowContext === 'settings' ? 'Change password' : 'Reset Password'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#8E8E93] hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step: Contact Method */}
          {step === 'contact' && (
            <div className="space-y-6">
              {/* Profile Info */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[#2C2C2E]">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#3A3A3C]">
                  {profile.profileImage ? (
                    <img
                      src={profile.profileImage}
                      alt={profile.stageName}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-semibold text-white">
                      {profile.stageName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{profile.stageName}</p>
                  <p className="text-xs text-[#8E8E93]">
                    {flowContext === 'settings'
                      ? 'Verify your contact, then set a new password'
                      : 'Resetting password for this profile'}
                  </p>
                </div>
              </div>

              {/* Contact Method Tabs */}
              <div>
                <p className="text-sm text-[#8E8E93] mb-3">
                  Choose how you'd like to receive the verification code:
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setContactMethod('phone')}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-colors',
                      contactMethod === 'phone'
                        ? 'bg-[#F33363] text-white'
                        : 'bg-[#2C2C2E] text-[#8E8E93] hover:bg-[#3A3A3C]'
                    )}
                  >
                    <Phone className="h-4 w-4" />
                    Phone
                  </button>
                  <button
                    onClick={() => setContactMethod('email')}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-colors',
                      contactMethod === 'email'
                        ? 'bg-[#F33363] text-white'
                        : 'bg-[#2C2C2E] text-[#8E8E93] hover:bg-[#3A3A3C]'
                    )}
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </button>
                </div>
              </div>

              {/* Input */}
              {contactMethod === 'phone' ? (
                <PhoneInput
                  ref={phoneRef}
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  error={errors.phone}
                />
              ) : (
                <Input
                  ref={emailRef}
                  label="Email Address"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={errors.email}
                />
              )}

              <Button
                variant="active"
                className="w-full"
                onClick={handleSendOTP}
                isLoading={isLoading}
              >
                Get OTP
              </Button>
            </div>
          )}

          {/* Step: OTP Verification */}
          {step === 'otp' && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-[#8E8E93]">
                  We've sent a 4-digit code to{' '}
                  {contactMethod === 'phone'
                    ? `+91 ${formatPhoneDisplay(phoneNumber)}`
                    : maskEmail(email)
                  }
                </p>
              </div>

              <OTPInput
                length={4}
                value={otp}
                onChange={setOtp}
                onComplete={handleVerifyOTP}
                autoFocus
              />

              {errors.otp && (
                <p className="text-xs text-destructive text-center">{errors.otp}</p>
              )}

              <div className="text-center">
                <p className="text-sm text-[#8E8E93]">
                  Didn't receive the code?{' '}
                  <button
                    className="font-semibold text-[#F33363] hover:text-[#D92D58] transition-colors"
                    onClick={handleSendOTP}
                    disabled={isLoading}
                  >
                    Resend OTP
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Step: New Password */}
          {step === 'password' && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-[#8E8E93]">
                  Create a new password for your account
                </p>
              </div>

              <div className="space-y-4">
                <Input
                  ref={newPasswordRef}
                  label="New Password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={errors.newPassword}
                  showPasswordToggle
                  showPasswordStrength
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={errors.confirmPassword}
                  showPasswordToggle
                />
              </div>

              <Button
                variant="active"
                className="w-full"
                onClick={handleResetPassword}
                isLoading={isLoading}
              >
                {flowContext === 'settings' ? 'Update password' : 'Reset Password'}
              </Button>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center py-8 space-y-4">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {flowContext === 'settings' ? 'Password updated' : 'Password Reset Successful!'}
                </h3>
                <p className="text-sm text-[#8E8E93]">
                  {flowContext === 'settings'
                    ? 'Your password has been updated. Redirecting...'
                    : 'Your password has been updated. Redirecting to sign in...'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
